import { useEffect, useState } from 'react';
import moment from 'moment';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import {
  getWorkplaceReservations,
  type WorkplaceReservation,
  type WorkplaceReservationsFailure,
} from '../services/floorplan-editor.service';

/**
 * How far ahead the detail list reaches, in days.
 *
 * Matches the server's own `MaxRangeInDays`. Asking for more is pointless — it clamps — and
 * asking for less would hide bookings it would happily have returned.
 */
export const IMPACT_LIST_DAYS = 366;

export interface RetirementImpact {
  /**
   * Every booking on or after the chosen date, with no upper bound at all.
   *
   * This is the authoritative number and the one the warning must be built on. The list below
   * is capped at a year, and a desk in this data already carries bookings 1.6 years out — a
   * warning that silently stopped at the cap would read as complete while reporting a subset.
   */
  readonly total: number;
  /** The bookings actually shown, which may be fewer than `total`. */
  readonly reservations: WorkplaceReservation[];
  /** True when the list is shorter than the total, so the dialog must say so out loud. */
  readonly truncated: boolean;
  /** Last day the list covers, `yyyy-MM-dd`. */
  readonly listEnd: string;
}

export interface RetirementImpactState {
  readonly loading: boolean;
  readonly data?: RetirementImpact;
  readonly error?: WorkplaceReservationsFailure;
}

/** `yyyy-MM-dd`, which is what the endpoint's from/to expect. */
const asQueryDate = (date: Date): string => moment(date).format('YYYY-MM-DD');

/**
 * What retiring this desk on this date would affect.
 *
 * Two calls on purpose, answering two different questions (see DESK_RETIREMENT_PLAN.md 3.3):
 *
 * - the **count**, sent as `from` with no `to`, uncapped and exempt from the server's 366-day
 *   limit. This is what the warning is built on, because it cannot miss anything.
 * - the **list**, sent as `from` and `to`, capped, for the names and dates to show.
 *
 * They are separate because the count has to be complete and the list has to be bounded, and
 * one request cannot be both. Where they disagree the dialog says so rather than presenting a
 * truncated list as the whole picture.
 */
export const useRetirementImpact = (
  workplaceId: string | undefined,
  /** The proposed retirement date, `yyyy-MM-dd`. */
  retiresOn: string | undefined,
  enabled: boolean,
): RetirementImpactState => {
  const keycloak = useKeycloak();
  const [data, setData] = useState<RetirementImpact | undefined>();
  const [error, setError] = useState<WorkplaceReservationsFailure | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !workplaceId || !retiresOn) {
      // Cleared when the dialog closes or the date is unset, so reopening cannot show the
      // previous desk's people — or the previous date's count — before its own answer lands.
      setData(undefined);
      setError(undefined);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError(undefined);

    const listEnd = asQueryDate(moment(retiresOn, 'YYYY-MM-DD').add(IMPACT_LIST_DAYS - 1, 'days').toDate());

    Promise.all([
      // `from` with no `to` is what asks for the uncapped total.
      getWorkplaceReservations(workplaceId, keycloak, { from: retiresOn }),
      getWorkplaceReservations(workplaceId, keycloak, { from: retiresOn, to: listEnd }),
    ])
      .then(([countResult, listResult]) => {
        if (!active) return;

        if (countResult.status !== 'ok' || listResult.status !== 'ok') {
          const failure = countResult.status !== 'ok' ? countResult.status : listResult.status;

          setData(undefined);
          // Never fall back to the capped list's own length as a stand-in total. Reporting
          // "12 bookings" when the real answer is 37 is worse than admitting the check failed,
          // because the administrator would act on it.
          setError(failure as WorkplaceReservationsFailure);
          return;
        }

        const { reservations } = listResult.data;
        const total = countResult.data.totalFromWindowStart ?? reservations.length;

        setData({
          total,
          reservations,
          truncated: total > reservations.length,
          listEnd,
        });
        setError(undefined);
      })
      .catch(() => {
        if (!active) return;

        setData(undefined);
        setError('failed');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [workplaceId, retiresOn, enabled, keycloak]);

  return { loading, data, error };
};

export default useRetirementImpact;
